/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Routes } from "@angular/router";
import { AnomaliesDetectionDetailsComponent } from "./pages/anomalies-detection-details/anomalies-detection-details.component";
import { AnomaliesDetectionComponent } from "./pages/anomalies-detection/anomalies-detection.component";
import { CalculatorHardwareAssetsComponent } from "./pages/calculator/calculator-hardware-assets/calculator-hardware-assets.component";
import { CalculatorSoftwareAssetsComponent } from "./pages/calculator/calculator-software-assets/calculator-software-assets.component";
import { CalculatorComponent } from "./pages/calculator/calculator.component";
import { AiPageComponent } from "./pages/dashboard/ai/ai.component";
import { CloudApplicationsComponent } from "./pages/dashboard/cloud-assets/cloud-applications/cloud-applications.component";
import { CloudAssetsComponent } from "./pages/dashboard/cloud-assets/cloud-assets.component";
import { CloudEnvironmentalImpactComponent } from "./pages/dashboard/cloud-assets/cloud-environmental-impact/cloud-environmental-impact.component";
import { CloudLocationsComponent } from "./pages/dashboard/cloud-assets/cloud-locations/cloud-locations.component";
import { DashboardComponent } from "./pages/dashboard/dashboard.component";
import { DataCentersComponent } from "./pages/dashboard/data-centers/data-centers.component";
import { HardwareAssetsRealtimeComponent } from "./pages/dashboard/hardware-assets-realtime/hardware-assets-realtime.component";
import { HardwareAssetsComponent } from "./pages/dashboard/hardware-assets/hardware-assets.component";
import { ImpactComponent } from "./pages/dashboard/impact/impact.component";
import { ComponentEmissionsComponent } from "./pages/dashboard/software-assets/component-emissions/component-emissions.component";
import { EnergyConsumptionComponent } from "./pages/dashboard/software-assets/energy-consumption/energy-consumption.component";
import { EnvironmentalImpactComponent } from "./pages/dashboard/software-assets/environmental-impact/environmental-impact.component";
import { SoftwareAssetsComponent } from "./pages/dashboard/software-assets/software-assets.component";
import { SoftwareCarbonIntensityComponent } from "./pages/dashboard/software-assets/software-carbon-intensity/software-carbon-intensity.component";
import { PageNotFoundComponent } from "./pages/page-not-found/page-not-found.component";
import { PrivacyStatementComponent } from "./pages/privacy-statement/privacy-statement.component";
import { RegionalEmissionsComponent } from "./pages/regional-emissions/regional-emissions.component";
import { StrategicApplicationsComponent } from "./pages/strategic-applications/strategic-applications.component";
import { TermsOfUseComponent } from "./pages/terms-of-use/terms-of-use.component";
import { ToolboxComponent } from "./pages/toolbox/toolbox.component";
import { UsersComponent } from "./pages/users/users.component";
import { WhatIfConfigurationComponent } from "./pages/what-if-configuration/what-if-configuration.component";
import { WhatifHardwareAssetsComponent } from "./pages/whatif/whatif-hardware-assets/whatif-hardware-assets.component";
import { WhatifSoftwareAssetsComponent } from "./pages/whatif/whatif-software-assets/whatif-software-assets.component";
import { WhatifComponent } from "./pages/whatif/whatif.component";
import { WorkInProgressComponent } from "./pages/work-in-progress/work-in-progress.component";
import { adminGuard } from "./shared/guards/admin.guard";
import { grantGuard } from "./shared/guards/grant.guard";
import { PATHS } from "./shared/utils/constants";
import { GRANTS } from "./shared/utils/grants.constants";

export const routes: Routes = [
	{
		path: "",
		pathMatch: "full",
		redirectTo: PATHS.DASHBOARD
	},
	{
		path: PATHS.DASHBOARD,
		component: DashboardComponent,
		children: [
			{ path: "", pathMatch: "full", redirectTo: PATHS.IMPACT },
			{
				path: PATHS.IMPACT,
				component: ImpactComponent,
				canActivate: [grantGuard],
				data: { grant: "impact:tab" }
			},
			{
				path: PATHS.STRATEGIC_APPLICATIONS,
				component: StrategicApplicationsComponent,
				canActivate: [grantGuard],
				data: { grant: "swabl:tab" }
			},
			{
				path: PATHS.SOFTWARE_ASSETS,
				component: SoftwareAssetsComponent,
				canActivate: [grantGuard],
				data: { grant: "swart:tab" },
				children: [
					{ path: "", pathMatch: "full", redirectTo: PATHS.ENVIRONMENTAL_IMPACT },
					{ path: PATHS.ENVIRONMENTAL_IMPACT, component: EnvironmentalImpactComponent },
					{ path: PATHS.COMPONENT_EMISSIONS, component: ComponentEmissionsComponent },
					{
						path: PATHS.SOFTWARE_CARBON_INTENSITY,
						component: SoftwareCarbonIntensityComponent
					},
					{ path: PATHS.ENERGY_CONSUMPTION, component: EnergyConsumptionComponent }
				]
			},
			{
				path: PATHS.DATA_CENTRE,
				component: DataCentersComponent,
				canActivate: [grantGuard],
				data: { grant: "dc:tab" }
			},
			{
				path: PATHS.CLOUD,
				component: CloudAssetsComponent,
				canActivate: [grantGuard],
				data: { grant: "cloud:tab" },
				children: [
					{ path: "", pathMatch: "full", redirectTo: PATHS.ENVIRONMENTAL_IMPACT },
					{
						path: PATHS.ENVIRONMENTAL_IMPACT,
						component: CloudEnvironmentalImpactComponent
					},
					{ path: PATHS.APPLICATIONS, component: CloudApplicationsComponent },
					{ path: PATHS.LOCATIONS, component: CloudLocationsComponent }
				]
			},
			{
				path: PATHS.HARDWARE_ASSETS,
				component: HardwareAssetsComponent,
				canActivate: [grantGuard],
				data: { grant: "hwabl:tab" }
			},
			{
				path: PATHS.HARDWARE_ASSETS_REALTIME,
				component: HardwareAssetsRealtimeComponent,
				canActivate: [grantGuard],
				data: { grant: "hwart:tab" }
			},
			{ path: PATHS.NETWORK, component: WorkInProgressComponent },
			{
				path: PATHS.AI,
				component: AiPageComponent,
				canActivate: [grantGuard],
				data: { grant: "ai:tab" }
			}
		]
	},
	{ path: PATHS.TOOLBOX, component: ToolboxComponent },
	{
		path: PATHS.STRATEGIC_APPLICATIONS,
		component: StrategicApplicationsComponent,
		canActivate: [grantGuard],
		data: { grant: "swabl:tab" }
	},
	{
		path: PATHS.WHAT_IF_CONFIGURATION,
		component: WhatIfConfigurationComponent,
		canActivate: [grantGuard],
		data: { grant: GRANTS.toolbox.whatIf }
	},
	{
		path: PATHS.WHAT_IF,
		component: WhatifComponent,
		canActivate: [grantGuard],
		data: { grant: GRANTS.toolbox.whatIf },
		children: [
			{ path: "", pathMatch: "full", redirectTo: PATHS.SOFTWARE_ASSETS },
			{
				path: PATHS.SOFTWARE_ASSETS,
				component: WhatifSoftwareAssetsComponent
			},
			{
				path: PATHS.HARDWARE_ASSETS,
				component: WhatifHardwareAssetsComponent
			}
		]
	},
	{
		path: PATHS.CALCULATOR,
		component: CalculatorComponent,
		canActivate: [grantGuard],
		data: { grant: GRANTS.toolbox.calculator },
		children: [
			{ path: "", pathMatch: "full", redirectTo: PATHS.SOFTWARE_ASSETS },
			{ path: PATHS.SOFTWARE_ASSETS, component: CalculatorSoftwareAssetsComponent },
			{ path: PATHS.HARDWARE_ASSETS, component: CalculatorHardwareAssetsComponent }
		]
	},
	{
		path: PATHS.ANOMALIES_DETECTION,
		component: AnomaliesDetectionComponent,
		canActivate: [grantGuard],
		data: { grant: GRANTS.toolbox.anomaliesDetection }
	},
	{
		path: PATHS.ANOMALIES_DETECTION_DETAILS,
		component: AnomaliesDetectionDetailsComponent,
		canActivate: [grantGuard],
		data: { grant: GRANTS.toolbox.anomaliesDetection }
	},
	{ path: PATHS.REGIONAL_EMISSIONS, component: RegionalEmissionsComponent },
	{ path: PATHS.USERS, component: UsersComponent, canActivate: [adminGuard] },
	// { path: PATHS.ALERTS, component: WorkInProgressComponent },
	{ path: PATHS.PRIVACY_STATEMENT, component: PrivacyStatementComponent },
	{ path: PATHS.TERMS_OF_USE, component: TermsOfUseComponent },
	{ path: "**", component: PageNotFoundComponent }
];
